/* TabbedAccordionComponent template */
import {FunctionalComponent, h, Host, JSX} from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars
import {JSXRender} from '@cewe-designsystem/abstract';
import {PlaceholderIcons} from '@cewe-designsystem/util_placeholder_icons';
import {TabbedAccordionComponent} from './cwc-tabbed-accordion';

interface TabbedAccordionTabData {
  title: string;
  id: string;
  name: string;
  icon: Element;
  active: boolean;
  index: number;
}

const defaultMobileIcon = () => {
  return (
    <svg class={'icon'} viewBox={`0 0 24 24`}>
      {PlaceholderIcons.svgAdd()}
    </svg>
  );
};

const getMobileIcon = (icon: Element) => {
  icon.classList.add('icon');
  return JSXRender(icon.outerHTML);
};

/**
 * Definition of  parameters that the template needs to work.
 */
interface TabbedAccordionTemplateProps {
  /**
   * The class instance that provides controller functionality for the template
   */
  controller: TabbedAccordionComponent;
  mobileIcon: Element;
  // Specify parameters that are required to be passed down to the template here.
}

const TabbedAccordionTemplate: FunctionalComponent<TabbedAccordionTemplateProps> = ({ controller, mobileIcon }) => (
  <Host>
    {controller.tabs.map((current: HTMLElement, index) => {
      const tabData: TabbedAccordionTabData = {
        title: current.dataset.title,
        id: controller.dataId + '-' + index,
        name: controller.dataId,
        icon: mobileIcon ? getMobileIcon(mobileIcon) : defaultMobileIcon(),
        active: current.dataset.active !== undefined,
        index: index,
      };

      return <TabTemplate controller={controller} data={tabData} content={current.innerHTML} />;
    })}
    <ul part="arrows">
      <li onClick={() => controller.onClickPrev()} part="arrow-left" data-hidden>
        <slot name={'action-left-icon'}>
          <svg viewBox={`0 0 24 24`}>{PlaceholderIcons.svgArrowLeft()}</svg>
        </slot>
      </li>
      <li onClick={() => controller.onClickNext()} part="arrow-right">
        <slot name={'action-right-icon'}>
          <svg viewBox={`0 0 24 24`}>{PlaceholderIcons.svgArrowRight()}</svg>
        </slot>
      </li>
    </ul>
    <hr />
  </Host>
);

/**
 * Definition of  parameters that the template needs to work.
 */
interface TabTemplateProps {
  /**
   * The class instance that provides controller functionality for the template
   */
  controller: TabbedAccordionComponent;
  data: TabbedAccordionTabData;
  content: JSX.Element;
  // Specify parameters that are required to be passed down to the template here.
}

export const TabTemplate: FunctionalComponent<TabTemplateProps> = ({ controller, data, content }) => {
  return [
    <input type="radio" id={data.id} name={data.name} />,
    <label htmlFor={data.id} data-first={data.index == 0} data-last={data.index == controller.tabs.length - 1}>
      <span>{data.title}</span>
      {data.icon}
    </label>,
    <cwc-tabbed-accordion-tab data-active={!data.active} part="cwc-tabbed-accordion-tab">
      {JSXRender(content)}
    </cwc-tabbed-accordion-tab>,
  ];
};

export default TabbedAccordionTemplate;
